/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

// Import polyfills for ES features and async/await
import "core-js/stable"
import "regenerator-runtime/runtime"
// Import all the third party stuff
import { datadogRum } from "@datadog/browser-rum"
import { GoogleOAuthProvider } from "@react-oauth/google"
import { ThemeProvider, createTheme } from "@mui/material/styles"
import { muiTheme } from "@rentspree/component-2023.theme.mui"
import { setOption } from "@rentspree/cookie"
import { ConnectedRouter } from "connected-react-router/immutable"
import React from "react"
import { QueryClient, QueryClientProvider } from "react-query"
import ReactDOM from "react-dom"
import { Provider } from "react-redux"
// Import root app
import { COOKIE_OPTION } from "constants/cookie"
import { AuthProvider } from "containers/auth-provider/auth-provider"
import { FeatureFlagProvider } from "containers/feature-flag/feature-flag-provider"
import LanguageProvider from "containers/language-provider"
import Router from "containers/router"
import Toast from "containers/toast"
import "utils/feedback-collection/instance"
import history from "utils/history"
import { parse } from "utils/query-parser"
import { getSubdomain } from "utils/subdomain"

import packageJson from "../package.json"
// Import i18n messages
import {
  API_URL,
  DATADOG_APPLICATION_ID,
  DATADOG_CLIENT_TOKEN,
  DATADOG_REPLAY_SAMPLE_RATE,
  DATADOG_SAMPLE_RATE,
  DATADOG_SITE,
  DATADOG_TRACE_SAMPLE_RATE,
  DATADOG_VERSION,
  ENVIRONMENT,
  GOOGLE_CLIENT_ID,
  PARTNER_ADMIN_URL,
  USER_API_URL
} from "./env"
import { translationMessages } from "./i18n"
import { store } from "./store"
import "./styles/sanitize.css"
/* eslint-disable import/no-unresolved, import/extensions */
import "./.htaccess"
/* eslint-enable import/no-unresolved, import/extensions */

const MOUNT_NODE = document.getElementById("app")

// Set option preparing to remove cookies
setOption(COOKIE_OPTION)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false
    },
    mutations: {
      retry: false
    }
  }
})

// Initiate DataDog RUM
if (DATADOG_APPLICATION_ID && DATADOG_CLIENT_TOKEN && DATADOG_SITE) {
  datadogRum.init({
    applicationId: DATADOG_APPLICATION_ID,
    clientToken: DATADOG_CLIENT_TOKEN,
    site: DATADOG_SITE,
    service: packageJson.name,
    env: ENVIRONMENT,
    version: DATADOG_VERSION,
    sessionSampleRate: DATADOG_SAMPLE_RATE,
    sessionReplaySampleRate: DATADOG_REPLAY_SAMPLE_RATE,
    traceSampleRate: DATADOG_TRACE_SAMPLE_RATE,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input",
    allowedTracingUrls: [
      { match: API_URL, propagatorTypes: ["b3", "b3multi", "tracecontext"] },
      {
        match: USER_API_URL,
        propagatorTypes: ["b3", "b3multi", "tracecontext"]
      },
      {
        match: PARTNER_ADMIN_URL,
        propagatorTypes: ["b3", "b3multi", "tracecontext"]
      }
    ],
    excludedActivityUrls: [
      /^https:\/\/api\.amplitude\.com/,
      /^https:\/\/analytics\.google\.com/,
      /^https:\/\/.*\.clarity\.ms\/collect/,
      /^https:\/\/settings\.luckyorange\.net/
    ],
    enableExperimentalFeatures: ["feature_flags"]
  })
}

const theme = createTheme(muiTheme)

const render = messages => {
  const url = parse(history.location.search).continue || null
  const subdomain = getSubdomain(url)

  ReactDOM.render(
    <Provider store={store}>
      <LanguageProvider messages={messages}>
        <QueryClientProvider client={queryClient}>
          <ConnectedRouter history={history}>
            <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
              <FeatureFlagProvider>
                <AuthProvider subdomain={subdomain}>
                  <ThemeProvider theme={theme}>
                    <Toast />
                    <Router history={history} subdomain={subdomain} />
                  </ThemeProvider>
                </AuthProvider>
              </FeatureFlagProvider>
            </GoogleOAuthProvider>
          </ConnectedRouter>
        </QueryClientProvider>
      </LanguageProvider>
    </Provider>,
    MOUNT_NODE
  )
}

if (module.hot) {
  // Hot reloadable React components and translation json files
  // modules.hot.accept does not accept dynamic dependencies,
  // have to be constants at compile-time
  module.hot.accept(["./i18n", "containers/router"], () => {
    ReactDOM.unmountComponentAtNode(MOUNT_NODE)
    render(translationMessages)
  })
}

// Chunked polyfill for browsers without Intl support
if (!window.Intl) {
  new Promise(resolve => {
    resolve(import("intl"))
  })
    .then(() => Promise.all([import("intl/locale-data/jsonp/en.js")]))
    .then(() => render(translationMessages))
    .catch(err => {
      throw err
    })
} else {
  render(translationMessages)
}
